import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { SreenCoverLoading, useRadioBoxGroup, usePopupPage, Input } from "src/components";
import {
  AnnouncementAgreePopupContents,
  ClubFindPopupContents,
} from "src/components/PopupPageContents";
import { usePopupSelectV2 } from "src/components/PopupSelect";
import SignModalContent from "src/components/YearBooking/SignModalContent";

import { useAlert, useGroupLocation, useUserStepStatusYear } from "src/hooks";
import AlertModal from "src/components/YearBooking/components/common/AlertModal";
import { createTeeshotAxiosInstance } from "src/http";
import { recoilStates } from "src/recoil/recoilStates";
import STATIC_DATA from "src/staticData";
import { colors } from "src/styles/theme";
import { CheckSubmitType, ClubType, SelectedClubType } from "src/types";

import styled from "styled-components";

// Step Components
// import Step1BasicInfo from "./YearBookingRegisterSteps/Step1BasicInfo";
import Step2DateSelection from "./YearBookingRegisterSteps/Step2DateSelection";
import Step3ClubSelection from "./YearBookingRegisterSteps/Step3ClubSelection";
import Step4RoundOptions from "./YearBookingRegisterSteps/Step4RoundOptions";
import Step5PaymentOptions from "./YearBookingRegisterSteps/Step5PaymentOptions";

const times = [
  { id: 1, name: "06시대", str: "0600" },
  { id: 2, name: "07시대", str: "0700" },
  { id: 3, name: "08시대", str: "0800" },
  { id: 4, name: "09시대", str: "0900" },
  { id: 5, name: "10시대", str: "1000" },
  { id: 6, name: "11시대", str: "1100" },
  { id: 7, name: "12시대", str: "1200" },
  { id: 8, name: "13시대", str: "1300" },
  { id: 9, name: "14시대", str: "1400" },
  { id: 10, name: "15시대", str: "1500" },
  { id: 11, name: "16시대", str: "1600" },
  { id: 12, name: "17시대", str: "1700" },
  { id: 13, name: "18시대", str: "1800" },
  { id: 14, name: "19시대", str: "1900" },
  { id: 15, name: "20시대", str: "2000" },
  { id: 16, name: "21시대", str: "2100" },
];

const days = [
  { id: 1, name: "월" },
  { id: 2, name: "화" },
  { id: 3, name: "수" },
  { id: 4, name: "목" },
  { id: 5, name: "금" },
  { id: 6, name: "토" },
  { id: 7, name: "일" },
];

export default function YearBookingRegister() {
  const navigate = useNavigate();
  const [isGroupDataLoading, setIsGroupDataLoading] = useState(true);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const groupLocations = useGroupLocation();
  const [user] = useRecoilState(recoilStates.user);
  const [bookingApplication] = useRecoilState(recoilStates.bookingApplication);

  // AlertModal 상태 관리
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showAlreadyBookedModal, setShowAlreadyBookedModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const mainContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (groupLocations) {
      setIsGroupDataLoading(false);
    }
  }, [groupLocations]);

  // 스텝 변경 시 MainContent를 최상단으로 스크롤
  useEffect(() => {
    console.log("currentStep 변경됨:", currentStep);

    const scrollToTop = () => {
      console.log("MainContent 스크롤 처리");

      // MainContent 컨테이너 스크롤
      if (mainContentRef.current) {
        mainContentRef.current.scrollTop = 0;
        mainContentRef.current.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      }

      // 전체 페이지 스크롤도 함께 처리
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    };

    // 즉시 실행
    scrollToTop();

    // 컴포넌트 렌더링 완료 후에도 스크롤 실행
    setTimeout(scrollToTop, 100);
    setTimeout(scrollToTop, 300);
  }, [currentStep]);

  const userStep = useUserStepStatusYear();

  useEffect(() => {
    (async () => {
      if (!userStep) {
        return;
      }

      if (userStep !== "noBooking" && userStep !== "rejectedAllProposal") {
        setShowAlreadyBookedModal(true);
      }
    })();
  }, [bookingApplication, userStep]);

  // Shared state for all steps
  const [selectedClubs, setSelectedClubs] = useState<SelectedClubType[]>();
  const [announcementShowingClub, setAnnouncementShowingClub] = useState<ClubType>();
  const [isSignModalOpened, setIsSignModalOpened] = useState(false);
  const [signBase64, setSignBase64] = useState<string | null>(null);

  // Step 2: Date Selection - Custom day and time selection state
  const [selectedDayFirst, setSelectedDayFirst] = useState<string>("- 요일");
  const [selectedDaySecond, setSelectedDaySecond] = useState<string>("- 요일");
  const [selectedTimeFirst, setSelectedTimeFirst] = useState<string>("- 시");
  const [selectedTimeSecond, setSelectedTimeSecond] = useState<string>("- 시");

  // Step 1: Club Selection (기존 Step 3)
  // 골프장 선택 관련 상태는 이미 위에 정의되어 있음

  // Step 2: Basic Info (기존 Step 1)
  const { RadioBoxGroup: TeamRadioBoxGroup, activeIds: selectedTeamCountIds } = useRadioBoxGroup({
    items: [
      { id: 2, name: "2팀" },
      { id: 3, name: "3팀" },
      { id: 4, name: "4팀" },
      { id: 5, name: "5팀" },
      { id: 6, name: "6팀" },
      { id: 7, name: "7팀" },
      { id: 8, name: "8팀" },
      { id: 9, name: "9팀" },
      { id: 10, name: "10팀~" },
      { id: 15, name: "15팀~" },
    ],
    defaultSelectedIds: [2],
    multiLineColumnCount: 5,
  });

  const { RadioBoxGroup: MonthRadioBoxGroup, activeIds: selectedMonthIds } = useRadioBoxGroup({
    items: [
      { id: 1, name: "1월" },
      { id: 2, name: "2월" },
      { id: 3, name: "3월" },
      { id: 4, name: "4월" },
      { id: 5, name: "5월" },
      { id: 6, name: "6월" },
      { id: 7, name: "7월" },
      { id: 8, name: "8월" },
      { id: 9, name: "9월" },
      { id: 10, name: "10월" },
      { id: 11, name: "11월" },
      { id: 12, name: "12월" },
    ],
    multipleValue: true,
    multiLineColumnCount: 6,
  });

  // Step 3: Date Selection (기존 Step 2)
  const { RadioBoxGroup: WeekRadioGroupFirst, activeIds: selectedWeekIdsFirst } = useRadioBoxGroup({
    items: [
      { id: 1, name: "1주" },
      { id: 2, name: "2주" },
      { id: 3, name: "3주" },
      { id: 4, name: "4주" },
    ],
  });

  const { RadioBoxGroup: WeekRadioGroupSecond, activeIds: selectedWeekIdsSecond } =
    useRadioBoxGroup({
      items: [
        { id: 1, name: "1주" },
        { id: 2, name: "2주" },
        { id: 3, name: "3주" },
        { id: 4, name: "4주" },
      ],
    });

  const { RadioBoxGroup: DayRadioGroupFirst, activeIds: selectedDayIdsFirst } = useRadioBoxGroup({
    items: days,
  });

  const { RadioBoxGroup: DayRadioGroupSecond, activeIds: selectedDayIdsSecond } = useRadioBoxGroup({
    items: days,
  });

  const {
    PopupSelect: TimePopupSelectFirst,
    activeIds: activeTimeIdsFirst,
    propsToInject: propsToInjectFirst,
  } = usePopupSelectV2({
    defaultSelectIds: [1],
  });

  const {
    PopupSelect: TimePopupSelectSecond,
    activeIds: activeTimeIdsSecond,
    propsToInject: propsToInjectSecond,
  } = usePopupSelectV2({
    defaultSelectIds: [1],
  });

  // Step 4: Round Options (기존 Step 4)
  const { RadioBoxGroup: PlayTypeBoxGroup, activeIds: activePlayTypeIds } = useRadioBoxGroup({
    boxType: "box",
    items: [
      { id: 1, name: "신페리오" },
      { id: 2, name: "스트로크" },
      { id: 3, name: "매치플레이" },
    ],
  });

  const { RadioBoxGroup: TeeTypeBoxGroup, activeIds: activeTeeTypeIds } = useRadioBoxGroup({
    boxType: "box",
    items: STATIC_DATA.TEEBOX_TYPE_DATA,
  });

  const { RadioBoxGroup: EtcRequirementsBoxGroup, activeIds: activeEtcRequirementsIds } =
    useRadioBoxGroup({
      multipleValue: true,
      boxType: "box",
      items: STATIC_DATA.ETC_REQ_DATA,
    });

  const { RadioBoxGroup: AwardsBoxGroup, activeIds: activeAwardsIds } = useRadioBoxGroup({
    multipleValue: true,
    boxType: "box",
    multiLineColumnCount: 3,
    items: STATIC_DATA.AWARDS_DATA,
  });

  const { RadioBoxGroup: PricePerPersonBoxGroup, activeIds: activePricePerPersonId } =
    useRadioBoxGroup({
      boxType: "box",
      items: [
        { id: 1, name: "1만원" },
        { id: 2, name: "2만원" },
        { id: 3, name: "3만원" },
        { id: 4, name: "4만원" },
        { id: 5, name: "5만원+" },
      ],
    });

  // Step 5: Payment Options (기존 Step 5)
  const { RadioBoxGroup: MealActiveBoxGroup, activeIds: mealActiveIds } = useRadioBoxGroup({
    boxType: "box",
    items: [
      { id: 1, name: "유" },
      { id: 2, name: "무" },
    ],
  });

  const { RadioBoxGroup: MealTypeBoxGroup, activeIds: mealTypeActiveIds } = useRadioBoxGroup({
    boxType: "box",
    items: STATIC_DATA.MEAL_TYPE_DATA,
  });

  const { RadioBoxGroup: RoomActiveBoxGroup, activeIds: roomActiveIds } = useRadioBoxGroup({
    boxType: "box",
    items: [
      { id: 1, name: "유" },
      { id: 2, name: "무" },
    ],
  });

  const { RadioBoxGroup: RoomTypeBoxGroup, activeIds: roomTypeActiveIds } = useRadioBoxGroup({
    boxType: "box",
    items: STATIC_DATA.ROOM_TYPE_DATA,
  });

  const { RadioBoxGroup: ShopActiveBoxGroup, activeIds: shopActiveIds } = useRadioBoxGroup({
    boxType: "box",
    items: [
      { id: 1, name: "유" },
      { id: 2, name: "무" },
    ],
  });

  const { value: shopItemValue, onChange: handleShopItemValueChange } = Input.hooks.useInput("");

  const { RadioBoxGroup: PayTypeBoxGroup, activeIds: activePayTypeIds } = useRadioBoxGroup({
    boxType: "box",
    items: [
      { id: 1, name: "전체 결제" },
      { id: 2, name: "팀별 결제" },
    ],
  });

  const [_1, setBookingApplication] = useRecoilState(recoilStates.bookingApplication);
  const alert = useAlert();
  const { value: additionalCommentValue, onChange: onAdditionalCommentChange } =
    Input.hooks.useInput();
  const [currentClubPreferenceNumber, setCurrentClubPreferenceNumber] = useState(0);

  // Popup management
  const {
    PopupComponent: AnnouncePopupComp,
    openPopup: openAnnouncePopup,
    closePopup: closeAnnounce,
  } = usePopupPage("announcement");

  const {
    PopupComponent: ClubFindPopupComp,
    openPopup: openClubSearchPopup,
    closePopup: closeClubSearchPopup,
  } = usePopupPage("clubSearch");

  // Navigation functions
  const goToNextStep = () => {
    // 현재 스텝별 검증
    if (!validateCurrentStep()) {
      return;
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  // 현재 스텝 검증 함수
  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        // 골프장 선택 검증 (최소 2개)
        if (!selectedClubs || selectedClubs.length < 2) {
          setValidationMessage("최소 2개 이상의 골프장을 선택해주세요.");
          setShowValidationModal(true);
          return false;
        }
        if (!selectedTeamCountIds || selectedTeamCountIds.length === 0) {
          setValidationMessage("팀 수를 선택해주세요.");
          setShowValidationModal(true);
          return false;
        }
        break;

      // case 2:
      //   // 팀 수 선택 검증
      //   if (!selectedTeamCountIds || selectedTeamCountIds.length === 0) {
      //     setValidationMessage("팀 수를 선택해주세요.");
      //     setShowValidationModal(true);
      //     return false;
      //   }
      //   break;

      case 2:
        // 월 선택 검증
        if (!selectedMonthIds || selectedMonthIds.length === 0) {
          setValidationMessage("희망 월을 선택해주세요.");
          setShowValidationModal(true);
          return false;
        }

        // 1지망 주차 선택 검증
        if (!selectedWeekIdsFirst || selectedWeekIdsFirst.length === 0) {
          setValidationMessage("1지망 주차를 선택해주세요.");
          setShowValidationModal(true);
          return false;
        }

        // 2지망 주차 선택 검증
        if (!selectedWeekIdsSecond || selectedWeekIdsSecond.length === 0) {
          setValidationMessage("2지망 주차를 선택해주세요.");
          setShowValidationModal(true);
          return false;
        }

        // 1지망 요일 선택 검증
        if (!selectedDayFirst || selectedDayFirst === "- 요일") {
          setValidationMessage("1지망 요일을 선택해주세요.");
          setShowValidationModal(true);
          return false;
        }

        // 2지망 요일 선택 검증
        if (!selectedDaySecond || selectedDaySecond === "- 요일") {
          setValidationMessage("2지망 요일을 선택해주세요.");
          setShowValidationModal(true);
          return false;
        }

        // 1지망 시간 선택 검증
        if (!selectedTimeFirst || selectedTimeFirst === "- 시") {
          setValidationMessage("1지망 시간을 선택해주세요.");
          setShowValidationModal(true);
          return false;
        }

        // 2지망 시간 선택 검증
        if (!selectedTimeSecond || selectedTimeSecond === "- 시") {
          setValidationMessage("2지망 시간을 선택해주세요.");
          setShowValidationModal(true);
          return false;
        }
        break;

      case 3:
        // 플레이 타입 선택 검증
        if (!activePlayTypeIds || activePlayTypeIds.length === 0) {
          setValidationMessage("플레이 타입을 선택해주세요.");
          setShowValidationModal(true);
          return false;
        }

        // 티박스 타입 선택 검증
        if (!activeTeeTypeIds || activeTeeTypeIds.length === 0) {
          setValidationMessage("티박스 타입을 선택해주세요.");
          setShowValidationModal(true);
          return false;
        }

        // 1인당 비용 선택 검증
        if (!activePricePerPersonId || activePricePerPersonId.length === 0) {
          setValidationMessage("1인당 비용을 선택해주세요.");
          setShowValidationModal(true);
          return false;
        }
        break;

      case 4:
        // 결제 방식 선택 검증
        if (!activePayTypeIds || activePayTypeIds.length === 0) {
          setValidationMessage("결제 방식을 선택해주세요.");
          setShowValidationModal(true);
          return false;
        }
        break;
    }

    return true;
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step: number) => {
    setCurrentStep(step);
  };

  // Submit data construction
  const submitData = {
    clubs: selectedClubs?.map((c) => {
      return { id: c.id, preference: c.preference };
    }),
    teamCount: selectedTeamCountIds?.[0],
    monthIds: selectedMonthIds,
    dateFirst: {
      weeks: selectedWeekIdsFirst,
      day: [selectedDayFirst], // 선택된 요일 사용
      time: times.find((t) => t.name === selectedTimeFirst)?.str ?? null,
    },
    dateSecond: {
      weeks: selectedWeekIdsSecond,
      day: [selectedDaySecond], // 선택된 요일 사용
      time: times.find((t) => t.name === selectedTimeSecond)?.str ?? null,
    },
    pricePerPerson: activePricePerPersonId?.[0],
    playTypeId: activePlayTypeIds?.[0],
    teeTypeId: activeTeeTypeIds?.[0],
    etcRequirementIds: activeEtcRequirementsIds,
    awardsIds: activeAwardsIds?.length ? activeAwardsIds : null,
    mealId: mealActiveIds?.[0] === 1 ? mealTypeActiveIds?.[0] : null,
    roomId: roomActiveIds?.[0] === 1 ? roomTypeActiveIds?.[0] : null,
    shopItem: shopActiveIds?.[0] === 1 ? shopItemValue : null,
    paymentTypeId: activePayTypeIds?.[0],
    additionalComment: additionalCommentValue,
  };

  const checkSubmitDataObj: CheckSubmitType[] = [
    {
      idToFocus: "club-section",
      name: "희망골프장",
      checkCondition: Boolean(submitData?.clubs?.length && submitData?.clubs?.length > 1),
      additionalMsg: "최소 2개 골프장을 선택해야합니다.",
    },
    {
      idToFocus: "team-section",
      name: "신청 팀수",
      checkCondition: Boolean(submitData?.teamCount),
    },
    {
      idToFocus: "month-section",
      name: "신청 월",
      checkCondition: Boolean(submitData?.monthIds?.length),
    },
    {
      idToFocus: "date-one",
      name: "신청일자 - 1지망",
      checkCondition:
        Boolean(submitData?.dateFirst?.day?.length) &&
        Boolean(submitData?.dateFirst?.weeks?.length) &&
        Boolean(submitData?.dateFirst?.time),
    },
    {
      idToFocus: "date-two",
      name: "신청일자 - 2지망",
      checkCondition:
        Boolean(submitData?.dateSecond?.day?.length) &&
        Boolean(submitData?.dateSecond?.weeks?.length) &&
        Boolean(submitData?.dateSecond?.time),
    },
  ];

  const handleSubmit = async () => {
    setIsSubmitLoading(true);
    console.log("submitData", submitData);
    try {
      const res = await createTeeshotAxiosInstance().post("/yearBooking/booking", {
        submitData: { ...submitData, customerSignBase64: signBase64 },
      });

      if (res.data.success === false) {
        throw new Error();
      }

      console.log("res.data :", res.data);
      const fetchedData = res.data?.data;
      if (fetchedData) {
        setIsSubmitLoading(false);
        setBookingApplication(fetchedData);
        navigate("/year-booking/complete");
      } else {
        throw new Error("데이터 없음");
      }
    } catch (e) {
      console.log("e :", e);
      setErrorMessage("전송 실패");
      setShowErrorModal(true);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const getProgressWidth = () => {
    return (currentStep / totalSteps) * 100;
  };

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step3ClubSelection
            TeamRadioBoxGroup={TeamRadioBoxGroup}
            selectedTeamCountIds={selectedTeamCountIds}
            selectedClubs={selectedClubs}
            setSelectedClubs={setSelectedClubs}
            setCurrentClubPreferenceNumber={setCurrentClubPreferenceNumber}
            openClubSearchPopup={openClubSearchPopup}
            alert={alert}
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
          />
        );
      case 2:
        return (
          <Step2DateSelection
            MonthRadioBoxGroup={MonthRadioBoxGroup}
            WeekRadioGroupFirst={WeekRadioGroupFirst}
            WeekRadioGroupSecond={WeekRadioGroupSecond}
            DayRadioGroupFirst={DayRadioGroupFirst}
            DayRadioGroupSecond={DayRadioGroupSecond}
            TimePopupSelectFirst={TimePopupSelectFirst}
            TimePopupSelectSecond={TimePopupSelectSecond}
            propsToInjectFirst={propsToInjectFirst}
            propsToInjectSecond={propsToInjectSecond}
            times={times}
            selectedMonthIds={selectedMonthIds}
            selectedWeekIdsFirst={selectedWeekIdsFirst}
            selectedWeekIdsSecond={selectedWeekIdsSecond}
            selectedDayIdsFirst={selectedDayIdsFirst}
            selectedDayIdsSecond={selectedDayIdsSecond}
            activeTimeIdsFirst={activeTimeIdsFirst}
            activeTimeIdsSecond={activeTimeIdsSecond}
            selectedDayFirst={selectedDayFirst}
            selectedDaySecond={selectedDaySecond}
            selectedTimeFirst={selectedTimeFirst}
            selectedTimeSecond={selectedTimeSecond}
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
            onDaySelectFirst={(dayId: number) => {
              const dayName = days.find((d) => d.id === dayId)?.name;
              if (dayName) {
                setSelectedDayFirst(dayName);
              }
            }}
            onDaySelectSecond={(dayId: number) => {
              const dayName = days.find((d) => d.id === dayId)?.name;
              if (dayName) {
                setSelectedDaySecond(dayName);
              }
            }}
            onTimeSelectFirst={(timeName: string) => {
              setSelectedTimeFirst(timeName);
            }}
            onTimeSelectSecond={(timeName: string) => {
              setSelectedTimeSecond(timeName);
            }}
          />
        );
      case 3:
        return (
          <Step4RoundOptions
            PlayTypeBoxGroup={PlayTypeBoxGroup}
            TeeTypeBoxGroup={TeeTypeBoxGroup}
            EtcRequirementsBoxGroup={EtcRequirementsBoxGroup}
            AwardsBoxGroup={AwardsBoxGroup}
            PricePerPersonBoxGroup={PricePerPersonBoxGroup}
            activePlayTypeIds={activePlayTypeIds}
            activeTeeTypeIds={activeTeeTypeIds}
            activeEtcRequirementsIds={activeEtcRequirementsIds}
            activeAwardsIds={activeAwardsIds}
            activePricePerPersonIds={activePricePerPersonId}
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
          />
        );
      case 4:
        return (
          <Step5PaymentOptions
            MealActiveBoxGroup={MealActiveBoxGroup}
            MealTypeBoxGroup={MealTypeBoxGroup}
            RoomActiveBoxGroup={RoomActiveBoxGroup}
            RoomTypeBoxGroup={RoomTypeBoxGroup}
            ShopActiveBoxGroup={ShopActiveBoxGroup}
            PayTypeBoxGroup={PayTypeBoxGroup}
            mealActiveIds={mealActiveIds}
            mealTypeActiveIds={mealTypeActiveIds}
            roomActiveIds={roomActiveIds}
            roomTypeActiveIds={roomTypeActiveIds}
            shopActiveIds={shopActiveIds}
            activePayTypeIds={activePayTypeIds}
            shopItemValue={shopItemValue}
            handleShopItemValueChange={handleShopItemValueChange}
            additionalCommentValue={additionalCommentValue}
            onAdditionalCommentChange={onAdditionalCommentChange}
            onPrevious={goToPreviousStep}
            onSubmit={() => setIsSignModalOpened(true)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Container>
        {/* Progress Bar */}
        <ProgressContainer>
          <ProgressBackground />
          <ProgressFill width={`${getProgressWidth()}%`} />
        </ProgressContainer>

        {/* Main Content */}
        <MainContent ref={mainContentRef}>{renderCurrentStep()}</MainContent>
      </Container>

      {/* Modals */}
      {isSignModalOpened && (
        <SignModalContent
          setSignBase64={setSignBase64}
          name={user?.realname}
          setModalState={setIsSignModalOpened}
          onConfirm={handleSubmit}
          rejectConfirmCondition={Boolean(!signBase64)}
        />
      )}

      <ClubFindPopupComp
        type={"back"}
        headerMsg={"골프장 검색"}
        content={
          <ClubFindPopupContents
            searchType="year"
            setAnnouncementShowingClub={setAnnouncementShowingClub}
            openAnnounceAgree={openAnnouncePopup}
            groupLocations={groupLocations}
          />
        }
      />

      <AnnouncePopupComp
        headerMsg={`${announcementShowingClub?.name} 모집공고`}
        type={"back"}
        content={
          <AnnouncementAgreePopupContents
            closeMe={closeAnnounce}
            closeParent={closeClubSearchPopup}
            announcementShowingClub={announcementShowingClub}
            preference={currentClubPreferenceNumber}
            setSelectedClubs={setSelectedClubs}
          />
        }
      />

      {showErrorModal && (
        <AlertModal
          onClose={() => setShowErrorModal(false)}
          title="전송 실패"
          subtitle={errorMessage}
          lines={["잠시 후 다시 시도해주세요."]}
          confirmText="확인"
          onConfirm={() => setShowErrorModal(false)}
        />
      )}

      {/* {showAlreadyBookedModal && (
        <AlertModal
          onClose={() => setShowAlreadyBookedModal(false)}
          title="이미 신청됨"
          subtitle="이미 부킹을 신청하셨습니다."
          lines={["이미 신청된 부킹이 있습니다."]}
          confirmText="확인"
          onConfirm={() => {
            setShowAlreadyBookedModal(false);
            navigate("/");
          }}
        />
      )} */}

      {showValidationModal && (
        <AlertModal
          onClose={() => setShowValidationModal(false)}
          title="입력 오류"
          subtitle={validationMessage}
          lines={["필수 항목을 입력해주세요."]}
          confirmText="확인"
          onConfirm={() => setShowValidationModal(false)}
        />
      )}

      {isSubmitLoading && <SreenCoverLoading />}
      {isGroupDataLoading && <SreenCoverLoading />}
    </>
  );
}

const Container = styled.div`
  width: 100%;
  height: 844px;
  background: #fdfdfd;
  position: relative;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  margin: 0 auto;
`;

const ProgressContainer = styled.div`
  width: 100%;
  height: 4px;
  position: absolute;
  left: 0px;
  top: 0;
`;

const ProgressBackground = styled.div`
  width: 100%;
  height: 4px;
  background: #d1d5d8;
  position: absolute;
  left: 0px;
  top: 0px;
`;

const ProgressFill = styled.div<{ width: string }>`
  width: ${(props) => props.width};
  height: 4px;
  background: #007bff;
  position: absolute;
  left: 0px;
  top: 0px;
  z-index: 1;
  transition: width 0.3s ease;
`;

const MainContent = styled.div`
  height: 100vh;
  overflow-y: auto;
  padding-top: 4px; /* Progress bar 공간 확보 */
  box-sizing: border-box;
`;

const Container2 = styled.div`
  .bottom-comment {
    background-color: ${colors.gray.fourth};
    margin-left: -15px;
    margin-right: -15px;
    margin-bottom: -20px;
    padding: 15px;
  }
`;
